import { useResizeObserver } from '@react-aria/utils';
import { useCallback, useState, ReactNode } from 'react';
import {
  ListBox,
  ListBoxItem,
  Popover,
  PopoverProps,
  Selection,
  Key
} from 'react-aria-components';

import { useDialogContext } from '@/fo/ui/kit/drawer/base';

import { styles } from './UiKitListboxBase.styles';

export interface BaseOption {
  label: string;
  value: string;
}

export type Option<T = object> = BaseOption & T;

export interface UiKitListboxBaseProps<T extends Option> extends PopoverProps {
  options: T[];
  renderOption?: (option: T) => ReactNode;
  selectionMode?: 'single' | 'multiple';
  selectedKeys?: Iterable<Key>;
  onSelectionChange?: (keys: Selection) => void;
  disabledKeys?: Iterable<Key>;
  isOpen?: boolean;
  renderListBox?: (options: T[], optionClassName: string) => ReactNode;
  width?: string | null;
  optionClassName?: string;
}

export function UiKitListboxBase<T extends Option>({
  options,
  triggerRef,
  renderOption,
  selectionMode = 'single',
  selectedKeys,
  onSelectionChange,
  disabledKeys,
  isOpen,
  renderListBox,
  width = null,
  optionClassName,
  ...props
}: UiKitListboxBaseProps<T>) {
  const slots = styles({ isEmpty: options.length === 0 });
  const optionClasses = slots.option({ className: optionClassName });

  const { contentRef: contentDialogRef } = useDialogContext();

  const [menuWidth, setMenuWidth] = useState<string | null>(width);

  const onResize = useCallback(() => {
    if (width) {
      setMenuWidth(width);
      return;
    }

    const calculateMenuWidth = () => {
      if (triggerRef?.current) {
        const triggerRect = triggerRef.current.getBoundingClientRect();
        setMenuWidth(`${triggerRect.width}px`);
      }
    };

    calculateMenuWidth();

    if (contentDialogRef) {
      setTimeout(() => {
        calculateMenuWidth();
      }, 100);
    }
  }, [triggerRef, contentDialogRef, setMenuWidth, width]);

  useResizeObserver({
    ref: triggerRef,
    onResize
  });

  return (
    <Popover
      triggerRef={triggerRef}
      isOpen={isOpen}
      className={slots.base()}
      UNSTABLE_portalContainer={contentDialogRef ?? undefined}
      offset={1}
      style={{ '--menu-width': menuWidth } as React.CSSProperties}
      {...props}
    >
      {renderListBox ? (
        renderListBox(options, optionClasses)
      ) : (
        <ListBox
          className={slots.listbox()}
          items={options}
          selectionMode={selectionMode}
          selectedKeys={selectedKeys}
          onSelectionChange={onSelectionChange}
          disabledKeys={disabledKeys}
        >
          {(item) => (
            <ListBoxItem
              key={item.value}
              id={item.value}
              className={optionClasses}
              textValue={item.label}
            >
              {renderOption ? renderOption(item) : item.label}
            </ListBoxItem>
          )}
        </ListBox>
      )}
    </Popover>
  );
}
